import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImagePlus, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText, media } from "@/lib/api";

export const confirmPublic = (v) => v !== "public" || window.confirm("Are you sure? Anyone on the internet will see this.");

export const PrivacySelect = ({ value, onChange, testid, allowPublic = true }) => (
  <select className="field" value={value || "hidden"} onChange={(e) => confirmPublic(e.target.value) && onChange(e.target.value)} data-testid={testid}>
    <option value="hidden">Hidden</option><option value="family">Family only</option>{allowPublic && <option value="public">Public</option>}
  </select>
);

export const PrivacyBadge = ({ v }) => (
  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${v === "public" ? "border-[#F2B8A0] text-[#F2B8A0]" : v === "family" ? "border-[#C9A24B] text-[#E4C87F]" : "border-[#F6EFE0]/40 text-[#F6EFE0]/75"}`}>{v === "family" ? "Family only" : v || "hidden"}</span>
);

export const uploadFile = async (file, extra = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries({ privacy: "family", in_gallery: "false", ...extra }).forEach(([k, v]) => fd.append(k, v));
  const r = await api.post("/admin/media", fd);
  return r.data;
};

export const MediaPicker = ({ open, onClose, onPick, multiple = false, initial = [] }) => {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSel(initial); api.get("/admin/media").then((r) => setList(r.data || [])).catch(() => {}); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const up = async (files) => {
    setBusy(true);
    try { for (const f of files) { const m = await uploadFile(f); setList((l) => [m, ...l]); } toast.success("Uploaded"); } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  const toggle = (id) => (multiple ? setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])) : (onPick(id), onClose()));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-[#0F2A1D] border-[#C9A24B]/40 text-[#F6EFE0] max-h-[85vh] overflow-y-auto" data-testid="media-picker">
        <DialogHeader><DialogTitle className="font-display text-2xl">Choose a picture</DialogTitle></DialogHeader>
        <label className="btn-ghost btn-sm w-fit cursor-pointer"><Upload size={14} /> {busy ? "Uploading..." : "Upload my family photo"}<input type="file" multiple accept="image/*,audio/*,video/*,application/pdf" hidden onChange={(e) => up([...e.target.files])} data-testid="media-picker-upload" /></label>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {list.map((m) => (
            <button key={m.id} type="button" onClick={() => toggle(m.id)} className={`relative aspect-square rounded overflow-hidden border-2 ${sel.includes(m.id) ? "border-[#C9A24B]" : "border-transparent"}`} data-testid={`media-pick-${m.id}`}>
              {m.kind === "image" ? <img src={media(m.url)} alt={m.caption} className="w-full h-full object-cover" /> : <span className="grid place-items-center h-full text-xs bg-[#163B29]">{m.kind}: {m.original_name}</span>}
            </button>
          ))}
        </div>
        {multiple && <button className="btn-gold w-fit" onClick={() => { onPick(sel); onClose(); }} data-testid="media-picker-done">Use {sel.length} selected</button>}
      </DialogContent>
    </Dialog>
  );
};

export const PhotoField = ({ value, url, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(url);
  const replace = async (file) => {
    if (!value) return;
    const fd = new FormData();
    fd.append("file", file);
    try { const r = await api.post(`/admin/media/${value}/replace`, fd); setPreview(`${r.data.url}&t=${Date.now()}`); toast.success("Replaced with your family photo"); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="w-24 h-16 rounded border border-[#C9A24B]/40 overflow-hidden bg-[#163B29]">{preview && <img src={media(preview)} alt="" className="w-full h-full object-cover" />}</div>
      <button type="button" className="btn-ghost btn-sm" onClick={() => setOpen(true)} data-testid={testid}><ImagePlus size={14} /> Choose</button>
      {value && <label className="btn-ghost btn-sm cursor-pointer">Replace with my family photo<input type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && replace(e.target.files[0])} /></label>}
      <MediaPicker open={open} onClose={() => setOpen(false)} onPick={(id) => { onChange(id); api.get("/admin/media").then((r) => setPreview((r.data || []).find((m) => m.id === id)?.url)); }} />
    </div>
  );
};

export const PhotosField = ({ value = [], onChange, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-[#F6EFE0]/80">{value.length} pictures</span>
      <button type="button" className="btn-ghost btn-sm" onClick={() => setOpen(true)} data-testid={testid}><ImagePlus size={14} /> Choose pictures</button>
      <MediaPicker open={open} multiple initial={value} onClose={() => setOpen(false)} onPick={onChange} />
    </div>
  );
};

export const FileField = ({ value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const up = async (f) => {
    setBusy(true);
    try { const m = await uploadFile(f); onChange(m.id); toast.success("File uploaded"); } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-[#F6EFE0]/80">{value ? "File attached" : "No file"}</span>
      <label className="btn-ghost btn-sm cursor-pointer"><Upload size={14} /> {busy ? "Uploading..." : "Upload"}<input type="file" hidden accept="audio/*,video/*,application/pdf,image/*" onChange={(e) => e.target.files[0] && up(e.target.files[0])} data-testid={testid} /></label>
      {value && <button type="button" className="lb-btn !w-8 !h-8" onClick={() => onChange(null)}><X size={12} /></button>}
    </div>
  );
};

export const MultiMember = ({ members, value = [], onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {members.map((m) => {
      const on = value.includes(m.id);
      return <button type="button" key={m.id} onClick={() => onChange(on ? value.filter((x) => x !== m.id) : [...value, m.id])} className={`text-xs px-3 py-1 rounded-full border ${on ? "bg-[#C9A24B] text-[#0B1D14] border-[#C9A24B]" : "border-[#C9A24B]/40"}`}>{m.name}</button>;
    })}
  </div>
);
